import { create } from 'zustand';
import { BusinessCard, CreateCardRequest, UpdateCardRequest } from '@/types';
import { apiService } from '@/services/api.service';
import { AsyncState } from './types';

interface CardsState extends AsyncState {
  // State
  cards: BusinessCard[];
  defaultCardId: number | null;
  maxCardsLimit: number;
  isLoaded: boolean;
  
  // Computed values
  defaultCard: BusinessCard | null;
  
  // Actions
  fetchCards: (accessToken: string, includeInactive?: boolean, forceRefresh?: boolean) => Promise<void>;
  getCardById: (cardId: number) => BusinessCard | undefined;
  createCard: (request: CreateCardRequest, accessToken: string) => Promise<number>;
  updateCard: (cardId: number, request: UpdateCardRequest, accessToken: string) => Promise<void>;
  deleteCard: (cardId: number, accessToken: string) => Promise<void>;
  setDefaultCard: (cardId: number, accessToken: string) => Promise<void>;
  deactivateCard: (cardId: number, accessToken: string) => Promise<void>;
  setError: (error: string | null) => void;
  reset: () => void;
  canCreateCard: () => boolean;
}

const initialState = {
  cards: [],
  defaultCardId: null,
  defaultCard: null,
  maxCardsLimit: 10,
  isLoaded: false,
  loading: false,
  error: null,
};

export const useCardsStore = create<CardsState>((set, get) => ({
  ...initialState,

  /**
   * Fetch all cards for the current user
   */
  fetchCards: async (accessToken: string, includeInactive = false, forceRefresh = false) => {
    // Skip if already loaded and not forcing refresh
    const { isLoaded, loading } = get();
    if (isLoaded && !loading && !forceRefresh) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await apiService.listCards(accessToken, includeInactive);
      
      const defaultCard = response.data.cards.find(
        c => c.cardId === response.data.defaultCardId
      ) || null;

      set({
        cards: response.data.cards,
        defaultCardId: response.data.defaultCardId,
        defaultCard,
        maxCardsLimit: response.data.maxCardsLimit || 10,
        isLoaded: true,
        loading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch cards';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      throw error;
    }
  },

  /**
   * Get a card by ID from the store
   */
  getCardById: (cardId: number) => {
    const { cards } = get();
    return cards.find(card => card.cardId === cardId);
  },

  /**
   * Create a new business card
   */
  createCard: async (request: CreateCardRequest, accessToken: string) => {
    set({ loading: true, error: null });
    try {
      const response = await apiService.createCard(request, accessToken);
      const newCardId = response.cardId;
      
      // Refresh cards list to include the new card with all its data
      await get().fetchCards(accessToken, false, true);
      
      set({ loading: false });
      return newCardId;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create card';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      throw error;
    }
  },

  /**
   * Update an existing business card
   */
  updateCard: async (cardId: number, request: UpdateCardRequest, accessToken: string) => {
    set({ loading: true, error: null });
    try {
      await apiService.updateCard(cardId, request, accessToken);
      
      // Refresh cards list to get updated data
      await get().fetchCards(accessToken, false, true);
      
      set({ loading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update card';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      throw error;
    }
  },

  /**
   * Delete a card permanently
   */
  deleteCard: async (cardId: number, accessToken: string) => {
    set({ loading: true, error: null });
    try {
      await apiService.deleteCard(cardId, accessToken);
      
      // Remove card from local state immediately for responsive UI
      const { cards, defaultCardId } = get();
      const updatedCards = cards.filter(card => card.cardId !== cardId);
      
      // If deleted card was default, clear defaultCardId
      const newDefaultCardId = cardId === defaultCardId ? null : defaultCardId;
      const newDefaultCard = updatedCards.find(c => c.cardId === newDefaultCardId) || null;
      
      set({
        cards: updatedCards,
        defaultCardId: newDefaultCardId,
        defaultCard: newDefaultCard,
        loading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete card';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      throw error;
    }
  },

  /**
   * Set a card as the default card
   */
  setDefaultCard: async (cardId: number, accessToken: string) => {
    set({ loading: true, error: null });
    try {
      await apiService.setDefaultCard(cardId, accessToken);
      
      // Update local state immediately - update isDefault on all cards
      const { cards } = get();
      const updatedCards = cards.map(card => ({
        ...card,
        isDefault: card.cardId === cardId,
      }));
      const defaultCard = updatedCards.find(c => c.cardId === cardId) || null;
      
      set({
        cards: updatedCards,
        defaultCardId: cardId,
        defaultCard,
        loading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to set default card';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      throw error;
    }
  },

  /**
   * Deactivate a card (soft delete)
   */
  deactivateCard: async (cardId: number, accessToken: string) => {
    set({ loading: true, error: null });
    try {
      await apiService.deactivateCard(cardId, accessToken);
      
      // Refresh cards to reflect deactivation
      await get().fetchCards(accessToken, false, true);
      
      set({ loading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to deactivate card';
      set({ 
        error: errorMessage, 
        loading: false 
      });
      throw error;
    }
  },

  /**
   * Set error message manually
   */
  setError: (error: string | null) => {
    set({ error });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },

  /**
   * Check if user can create more cards
   */
  canCreateCard: () => {
    const { cards, maxCardsLimit } = get();
    return cards.length < maxCardsLimit;
  },
}));
